import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Chip,
  CircularProgress
} from '@mui/material';
import { 
  LocationOn as LocationIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import UniversalMap from './maps/UniversalMap';
import { LatLng } from './maps/MapInterface';
import { useMemoryTypes } from '../hooks/useMemoryTypes';
import { US_STATES } from '../utils/usStates';

interface ExperienceData {
  title: string;
  description: string;
  location?: {
    lat: number;
    lng: number;
    address: string;
    placeId?: string;
  };
  time?: string;
  type: string;
  tags: string[];
  notes: string;
}

interface AddExperienceDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (experience: ExperienceData & { day: number }) => void;
  selectedDay: number;
  dayDate: Date;
  initialExperience?: ExperienceData & { id: string; day: number };
}

const AddExperienceDialog: React.FC<AddExperienceDialogProps> = ({
  open,
  onClose,
  onSave,
  selectedDay,
  dayDate,
  initialExperience
}) => {
  const { memoryTypes, loading: memoryTypesLoading } = useMemoryTypes();
  const [experienceData, setExperienceData] = useState<ExperienceData>({
    title: '',
    description: '',
    type: 'attraction',
    tags: [],
    notes: ''
  });
  
  const [tagInput, setTagInput] = useState('');

  // Search functionality - separate inputs like MapView
  const [cityValue, setCityValue] = useState('');
  const [stateValue, setStateValue] = useState('');
  const [businessValue, setBusinessValue] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [mapCenter, setMapCenter] = useState({ lat: 39.8283, lng: -98.5795 });
  const [mapZoom, setMapZoom] = useState(4);
  const [isSearchingBusiness, setIsSearchingBusiness] = useState(false);
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);

  // Initialize form with existing experience data when editing
  useEffect(() => {
    if (initialExperience) {
      setExperienceData({
        title: initialExperience.title,
        description: initialExperience.description,
        location: initialExperience.location,
        time: initialExperience.time,
        type: initialExperience.type,
        tags: initialExperience.tags || [],
        notes: initialExperience.notes
      });
    } else {
      // Reset form for new experience
      setExperienceData({
        title: '',
        description: '',
        type: 'attraction',
        tags: [],
        notes: ''
      });
      // Reset search fields
      setCityValue('');
      setStateValue('');
      setBusinessValue('');
      setSearchResults([]);
      setMapCenter({ lat: 39.8283, lng: -98.5795 });
      setMapZoom(4);
      setIsSearchingBusiness(false);
      setIsSearchingLocation(false);
    }
  }, [initialExperience, open]);

  const handleLocationSelect = (
    location: { lat: number; lng: number }, 
    address?: string, 
    placeId?: string
  ) => {
    // Update experience data with location
    setExperienceData(prev => ({
      ...prev,
      location: {
        lat: location.lat,
        lng: location.lng,
        address: address || `${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`,
        placeId
      }
    }));
    
    // Update map center and zoom to show the selected location
    setMapCenter({ lat: location.lat, lng: location.lng });
    setMapZoom(15); // Zoom in to show the specific location
  };

  // Search functionality similar to MapView
  const handleBusinessSearch = useCallback(async (business: string, city: string, state: string) => {
    if (!business.trim()) return;

    setIsSearchingBusiness(true);
    try {
      console.log('🔍 Experience Dialog: Searching for business:', business, city, state);
      
      // Build search query
      const searchQuery = [business, city, state].filter(Boolean).join(', ');
      
      // Use our search provider API
      const response = await fetch('/api/search-providers/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: searchQuery,
          city: city || undefined,
          state: state || undefined
        }),
      });

      if (!response.ok) {
        throw new Error(`Search failed: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('📍 Experience Dialog: Search results:', data);

      if (data.success && data.results?.length > 0) {
        setSearchResults(data.results);
        
        // Auto-select first result if there's only one
        if (data.results.length === 1) {
          const result = data.results[0];
          handleLocationSelect(
            { lat: result.lat, lng: result.lng },
            result.address || result.display_name,
            result.place_id
          );
          
          // Update experience title if not set
          if (!experienceData.title.trim()) {
            setExperienceData(prev => ({ ...prev, title: result.name || business }));
          }
        }
      } else {
        console.log('No results found for business search');
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Error searching for business:', error);
      setSearchResults([]);
    } finally {
      setIsSearchingBusiness(false);
    }
  }, [experienceData.title]);

  const handleLocationFilter = useCallback(async (city: string, state: string) => {
    if (!city.trim() && !state.trim()) return;

    setIsSearchingLocation(true);
    try {
      console.log('🔍 Experience Dialog: Searching for city/state:', city, state);
      
      const searchQuery = [city, state].filter(Boolean).join(', ');
      
      const response = await fetch('/api/search-providers/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: searchQuery,
          searchType: 'location'
        }),
      });

      if (!response.ok) {
        throw new Error(`Location search failed: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('📍 Experience Dialog: Location search results:', data);

      if (data.success && data.results?.length > 0) {
        const result = data.results[0];
        handleLocationSelect(
          { lat: result.lat, lng: result.lng },
          result.address || result.display_name
        );
      } else {
        console.log('No results found for location search');
      }
    } catch (error) {
      console.error('Error searching for location:', error);
    } finally {
      setIsSearchingLocation(false);
    }
  }, []);

  const handleSearchResultSelect = (result: any) => {
    handleLocationSelect(
      { lat: result.lat, lng: result.lng },
      result.address || result.display_name,
      result.place_id
    );
    
    // Update experience title if not set
    if (!experienceData.title.trim() && result.name) {
      setExperienceData(prev => ({ ...prev, title: result.name }));
    }
    
    // Clear search results
    setSearchResults([]);
  };



  const handleAddTag = () => {
    if (tagInput.trim() && !experienceData.tags.includes(tagInput.trim())) {
      setExperienceData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setExperienceData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSave = () => {
    if (!experienceData.title.trim()) {
      alert('Please enter a title for the experience');
      return;
    }

    // Prepare data for saving, stripping seconds from time if present
    const saveData = {
      ...experienceData,
      day: selectedDay,
      // Convert time from HH:MM:SS to HH:MM format
      time: experienceData.time ? experienceData.time.substring(0, 5) : undefined
    };

    onSave(saveData);

    // Reset form
    setExperienceData({
      title: '',
      description: '',
      type: 'attraction',
      tags: [],
      notes: ''
    });
    setTagInput('');
    setCityValue('');
    setStateValue('');
    setBusinessValue('');
    setSearchResults([]);
    setMapCenter({ lat: 39.8283, lng: -98.5795 });
    setMapZoom(4);
    setIsSearchingBusiness(false);
    setIsSearchingLocation(false);
    
    onClose();
  };

  const handleCancel = () => {
    // Reset form
    setExperienceData({
      title: '',
      description: '',
      type: 'attraction',
      tags: [],
      notes: ''
    });
    setTagInput('');
    setCityValue('');
    setStateValue('');
    setBusinessValue('');
    setSearchResults([]);
    setMapCenter({ lat: 39.8283, lng: -98.5795 });
    setMapZoom(4);
    setIsSearchingBusiness(false);
    setIsSearchingLocation(false);
    
    onClose();
  };



  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box>
          <Typography variant="h6" component="div">
            {initialExperience ? 'Edit Experience' : 'Add Experience'} - Day {selectedDay}
          </Typography>
          <Typography variant="subtitle2" color="textSecondary" component="div">
            {dayDate ? dayDate.toLocaleDateString('en-US', { 
              weekday: 'long', 
              month: 'long', 
              day: 'numeric', 
              year: 'numeric' 
            }) : 'Select a date'}
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {/* Basic Information */}
          <TextField
            fullWidth
            label="Experience Title"
            value={experienceData.title}
            onChange={(e) => setExperienceData(prev => ({ ...prev, title: e.target.value }))}
            required
          />
          
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Description"
            value={experienceData.description}
            onChange={(e) => setExperienceData(prev => ({ ...prev, description: e.target.value }))}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={experienceData.type}
                label="Type"
                onChange={(e) => setExperienceData(prev => ({ 
                  ...prev, 
                  type: e.target.value as ExperienceData['type']
                }))}
                disabled={memoryTypesLoading}
              >
                {memoryTypesLoading ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Loading...
                  </MenuItem>
                ) : (
                  memoryTypes.map((memoryType) => (
                    <MenuItem key={memoryType.id} value={memoryType.name}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {memoryType.icon && (
                          <Typography component="span" sx={{ fontSize: '1.1em' }}>
                            {memoryType.icon}
                          </Typography>
                        )}
                        {memoryType.display_name}
                      </Box>
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            
            <TextField
              type="time"
              label="Time (Optional)"
              value={experienceData.time || ''}
              onChange={(e) => setExperienceData(prev => ({ ...prev, time: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          {/* Location Search and Map */}
          <Box>
            <Typography variant="h6" gutterBottom>Location</Typography>
            
            {/* Search Inputs - matching MapView design */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                Search for Experience Location
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '3fr 2fr 2fr 1fr' }, gap: 2, alignItems: 'end' }}>
                <TextField
                  fullWidth
                  label="Business or Place"
                  placeholder="e.g., Starbucks, Disney World, Best Buy"
                  helperText="Restaurant, store, attraction, etc."
                  size="small"
                  value={businessValue}
                  onChange={(e) => setBusinessValue(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleBusinessSearch(businessValue, cityValue, stateValue);
                    }
                  }}
                />
                <TextField
                  fullWidth
                  label="City (Optional)"
                  placeholder="e.g., Orlando"
                  helperText="Narrow search to city"
                  size="small"
                  value={cityValue}
                  onChange={(e) => setCityValue(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      if (businessValue.trim()) {
                        handleBusinessSearch(businessValue, cityValue, stateValue);
                      } else {
                        handleLocationFilter(cityValue, stateValue);
                      }
                    }
                  }}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>State (Optional)</InputLabel>
                    <Select
                      label="State (Optional)"
                      value={stateValue}
                      onChange={(e) => setStateValue(e.target.value as string)}
                    >
                      <MenuItem value="">
                        <em>Any State</em>
                      </MenuItem>
                      {US_STATES.map(state => (
                        <MenuItem key={state.value} value={state.value}>
                          {state.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" sx={{ mt: 0.5, color: 'text.secondary' }}>
                    Select state filter
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={
                    (isSearchingBusiness || isSearchingLocation) ? 
                      <CircularProgress size={16} /> : 
                      <SearchIcon />
                  }
                  onClick={() => {
                    if (businessValue.trim()) {
                      handleBusinessSearch(businessValue, cityValue, stateValue);
                    } else {
                      handleLocationFilter(cityValue, stateValue);
                    }
                  }}
                  disabled={
                    (!businessValue.trim() && !cityValue.trim() && !stateValue.trim()) ||
                    isSearchingBusiness || 
                    isSearchingLocation
                  }
                  size="small"
                >
                  {(isSearchingBusiness || isSearchingLocation) ? 'Searching...' : 'Search'}
                </Button>
              </Box>
            </Box>

            {/* Search Status Indicator */}
            {(isSearchingBusiness || isSearchingLocation) && (
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  {isSearchingBusiness ? 'Searching for business...' : 'Searching for location...'}
                </Typography>
              </Box>
            )}

            {/* Search Results */}
            {searchResults.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Search Results ({searchResults.length})
                </Typography>
                <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                  {searchResults.map((result, index) => (
                    <Card 
                      key={index} 
                      sx={{ 
                        mb: 1, 
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'action.hover' }
                      }}
                      onClick={() => handleSearchResultSelect(result)}
                    >
                      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocationIcon fontSize="small" color="primary" />
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {result.name || 'Unknown Location'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {result.address || result.display_name || 'No address available'}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Box>
            )}
            
            {/* Map for final location selection */}
            <Card>
              <CardContent sx={{ p: 0 }}>
                <UniversalMap
                  center={mapCenter}
                  zoom={mapZoom}
                  style={{ height: '300px', width: '100%' }}
                  onMapClick={(latLng: LatLng) => {
                    handleLocationSelect(latLng);
                  }}
                  markers={experienceData.location ? [{
                    id: 'experience-location',
                    position: {
                      lat: experienceData.location.lat,
                      lng: experienceData.location.lng
                    },
                    title: experienceData.location.address || 'Selected Location'
                  }] : []}
                  showSearch={false}
                />
              </CardContent>
            </Card>
            
            {experienceData.location && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                <LocationIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                {experienceData.location.address}
              </Typography>
            )}
          </Box>

          {/* Tags */}
          <Box>
            <Typography variant="h6" gutterBottom>Tags</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              {experienceData.tags.map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  onDelete={() => handleRemoveTag(tag)}
                  size="small"
                />
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                placeholder="Add tag..."
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
              />
              <Button size="small" onClick={handleAddTag}>Add</Button>
            </Box>
          </Box>

          {/* Notes */}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Additional Notes"
            value={experienceData.notes}
            onChange={(e) => setExperienceData(prev => ({ ...prev, notes: e.target.value }))}
          />
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          Add Experience
        </Button>
      </DialogActions>
    </Dialog>
  );
};



export default AddExperienceDialog;